import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/features/auth/AuthProvider";

// Drop-in image slot. When `src` is provided it renders the photo;
// otherwise it falls back to a labeled gradient block so the page still
// reads well during the POC phase. Swap real photos in by passing `src`.
function PhotoSlot({
  src,
  alt,
  label,
  className = "",
  tone = "blue",
}: {
  src?: string;
  alt?: string;
  label: string;
  className?: string;
  tone?: "blue" | "green" | "ink";
}) {
  const tones: Record<string, string> = {
    blue: "from-brand-100 via-brand-50 to-white text-brand-900",
    green: "from-accent-100 via-accent-50 to-white text-accent-800",
    ink: "from-slate-200 via-slate-100 to-white text-ink",
  };
  if (src) {
    return (
      <img
        src={src}
        alt={alt ?? label}
        className={`h-full w-full object-cover ${className}`}
      />
    );
  }
  return (
    <div
      role="img"
      aria-label={label}
      className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${tones[tone]} ${className}`}
    >
      <div className="flex flex-col items-center gap-2 px-6 text-center">
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] opacity-70">
          Photo
        </span>
        <span className="text-sm font-medium">{label}</span>
      </div>
    </div>
  );
}

export function LandingPage() {
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();

  if (!isLoading && user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="-mx-4 -my-6 sm:mx-0">
      <Hero />
      <DesiredStateBand />
      <Pillars />
      <UseCases />
      <ClosingCTA />
    </div>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-brand-50 via-white to-accent-50"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 -top-32 -z-10 h-96 w-96 rounded-full bg-brand-200/40 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -left-32 -z-10 h-96 w-96 rounded-full bg-accent-200/40 blur-3xl"
      />

      <div className="mx-auto grid max-w-5xl gap-12 px-12 pb-16 pt-14 sm:px-16 sm:pt-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:px-24 lg:pb-24">
        <div className="flex flex-col justify-center">
          <span className="inline-flex w-fit items-center gap-2 rounded-full border border-brand-200 bg-white/70 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.16em] text-brand-700 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
            {t("landing.kicker", "Facility intelligence")}
          </span>

          <h1 className="mt-5 text-4xl font-semibold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            {t("landing.hero.title_pre", "Every facility has a ")}
            <span className="brand-wordmark">
              {t("landing.hero.title_em", "desired state")}
            </span>
            {t("landing.hero.title_post", ". Anything else is an anomaly.")}
          </h1>

          <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-soft">
            {t(
              "landing.hero.lede",
              "Describe what good looks like — every door, light, fence and pitch — and we keep it that way. Anyone with the link can flag a fault. An AI agent triages, deduplicates, and routes work to the people who can fix it.",
            )}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/register"
              className="inline-flex h-11 items-center justify-center rounded-md bg-brand-500 px-5 text-sm font-semibold text-white shadow-brand transition hover:bg-brand-600 active:bg-brand-700"
            >
              {t("landing.hero.cta_primary", "Set up your facility")}
            </Link>
            <Link
              to="/login"
              className="inline-flex h-11 items-center justify-center rounded-md bg-white px-5 text-sm font-semibold text-ink ring-1 ring-inset ring-slate-200 transition hover:bg-brand-50 hover:ring-brand-200"
            >
              {t("landing.hero.cta_secondary", "Sign in")}
            </Link>
          </div>

          <dl className="mt-10 grid max-w-md grid-cols-3 gap-6 text-sm">
            <div>
              <dt className="text-ink-soft">{t("landing.hero.stat1_label", "Setup")}</dt>
              <dd className="mt-1 font-semibold text-ink">
                {t("landing.hero.stat1_value", "Minutes")}
              </dd>
            </div>
            <div>
              <dt className="text-ink-soft">{t("landing.hero.stat2_label", "Reporter")}</dt>
              <dd className="mt-1 font-semibold text-ink">
                {t("landing.hero.stat2_value", "No login")}
              </dd>
            </div>
            <div>
              <dt className="text-ink-soft">{t("landing.hero.stat3_label", "Triage")}</dt>
              <dd className="mt-1 font-semibold text-ink">
                {t("landing.hero.stat3_value", "AI-assisted")}
              </dd>
            </div>
          </dl>
        </div>

        {/* Hero photo + annotation card. */}
        <div className="relative">
          <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-white shadow-xl shadow-brand-900/10 ring-1 ring-slate-200/70">
            <PhotoSlot
              label={t("landing.hero.photo", "Housing courtyard")}
              tone="blue"
            />
          </div>

          {/* Desired-state annotation chips overlay */}
          <div className="absolute -left-4 top-8 hidden max-w-[15rem] rounded-xl bg-white/95 p-3 shadow-lg ring-1 ring-slate-200/70 backdrop-blur sm:block">
            <div className="flex items-center gap-2 text-xs font-medium text-accent-700">
              <span className="h-2 w-2 rounded-full bg-accent-500" />
              {t("landing.hero.chip_ok", "Lighting · Healthy")}
            </div>
            <p className="mt-1 text-xs text-ink-soft">
              {t("landing.hero.chip_ok_note", "All 12 fixtures reported on, last 7 days.")}
            </p>
          </div>

          <div className="absolute -right-4 bottom-10 hidden max-w-[16rem] rounded-xl bg-white/95 p-3 shadow-lg ring-1 ring-slate-200/70 backdrop-blur sm:block">
            <div className="flex items-center gap-2 text-xs font-medium text-brand-700">
              <span className="h-2 w-2 rounded-full bg-brand-500" />
              {t("landing.hero.chip_fault", "Gate B · Anomaly")}
            </div>
            <p className="mt-1 text-xs text-ink-soft">
              {t(
                "landing.hero.chip_fault_note",
                "3 reports merged · routed to caretaker.",
              )}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DesiredStateBand() {
  const { t } = useTranslation();
  return (
    <section className="border-y border-slate-200/70 bg-white/60">
      <div className="mx-auto max-w-5xl px-4 py-10 sm:py-14">
        <p className="max-w-3xl text-lg leading-relaxed text-ink sm:text-xl">
          <span className="font-semibold">
            {t(
              "landing.band.lead",
              "A facility isn't a list of jobs — it's a promise.",
            )}
          </span>{" "}
          <span className="text-ink-soft">
            {t(
              "landing.band.body",
              "The lights work. The gate closes. The pitch is mowed. Facility Manager is built around that promise: define the desired state, let everyone help spot when reality drifts, and let the system coordinate the fix.",
            )}
          </span>
        </p>
      </div>
    </section>
  );
}

function Pillars() {
  const { t } = useTranslation();
  const items = [
    {
      title: t("landing.pillars.define.title", "Define the desired state"),
      body: t(
        "landing.pillars.define.body",
        "Map out the things that should work — buildings, equipment, areas. Add a photo, a position on the map, and what 'good' looks like.",
      ),
      icon: BlueprintIcon,
      tone: "blue" as const,
    },
    {
      title: t("landing.pillars.report.title", "Catch anomalies early"),
      body: t(
        "landing.pillars.report.body",
        "A QR code or link is all anyone needs. Residents, visitors, staff — they snap a photo, describe what's off, and you see it.",
      ),
      icon: SignalIcon,
      tone: "green" as const,
    },
    {
      title: t("landing.pillars.coordinate.title", "AI coordinates the fix"),
      body: t(
        "landing.pillars.coordinate.body",
        "Reports are deduplicated, categorised, prioritised, and turned into tasks for the right person. You stay in the loop, not in the weeds.",
      ),
      icon: AgentIcon,
      tone: "blue" as const,
    },
  ];

  return (
    <section className="mx-auto max-w-5xl px-4 py-16 sm:py-20">
      <div className="max-w-2xl">
        <p className="text-sm font-medium uppercase tracking-[0.16em] text-brand-700">
          {t("landing.pillars.kicker", "How it works")}
        </p>
        <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
          {t(
            "landing.pillars.title",
            "Three moves, one well-kept facility.",
          )}
        </h2>
      </div>

      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(({ title, body, icon: Icon, tone }) => (
          <article
            key={title}
            className="group relative overflow-hidden rounded-2xl bg-white p-6 ring-1 ring-slate-200/70 transition hover:-translate-y-0.5 hover:shadow-lg hover:ring-brand-200"
          >
            <div
              className={`mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl ${
                tone === "green"
                  ? "bg-accent-50 text-accent-700"
                  : "bg-brand-50 text-brand-700"
              }`}
            >
              <Icon />
            </div>
            <h3 className="text-lg font-semibold text-ink">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink-soft">{body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function UseCases() {
  const { t } = useTranslation();
  const cases = [
    {
      label: t("landing.cases.housing.label", "Housing co-ops"),
      caption: t(
        "landing.cases.housing.caption",
        "Entryways, laundry rooms, courtyards.",
      ),
      tone: "blue" as const,
    },
    {
      label: t("landing.cases.sports.label", "Sports clubs"),
      caption: t(
        "landing.cases.sports.caption",
        "Pitches, nets, floodlights, clubhouse.",
      ),
      tone: "green" as const,
    },
    {
      label: t("landing.cases.fence.label", "Estates & grounds"),
      caption: t(
        "landing.cases.fence.caption",
        "Fences, gates, paths, signage.",
      ),
      tone: "ink" as const,
    },
    {
      label: t("landing.cases.marina.label", "Marinas & yards"),
      caption: t(
        "landing.cases.marina.caption",
        "Jetties, cranes, power posts, parking.",
      ),
      tone: "blue" as const,
    },
  ];

  return (
    <section className="bg-white/70 py-16 sm:py-20">
      <div className="mx-auto max-w-5xl px-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-xl">
            <p className="text-sm font-medium uppercase tracking-[0.16em] text-accent-700">
              {t("landing.cases.kicker", "Built for shared places")}
            </p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
              {t(
                "landing.cases.title",
                "Anywhere a community shares responsibility.",
              )}
            </h2>
          </div>
          <p className="max-w-sm text-sm text-ink-soft">
            {t(
              "landing.cases.note",
              "Start with one site. Add buildings, items and areas as you go — the model bends to your facility, not the other way round.",
            )}
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {cases.map(({ label, caption, tone }) => (
            <figure
              key={label}
              className="group overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200/70 transition hover:shadow-lg hover:ring-brand-200"
            >
              <div className="aspect-[4/5]">
                <PhotoSlot label={label} tone={tone} />
              </div>
              <figcaption className="border-t border-slate-200/70 p-4">
                <div className="text-sm font-semibold text-ink">{label}</div>
                <div className="mt-1 text-xs leading-relaxed text-ink-soft">
                  {caption}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClosingCTA() {
  const { t } = useTranslation();
  return (
    <section className="px-4 py-16 sm:py-20">
      <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl bg-ink px-6 py-12 text-white shadow-xl sm:px-12 sm:py-16">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand-500/30 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 -left-24 h-80 w-80 rounded-full bg-accent-500/20 blur-3xl"
        />

        <div className="relative grid items-center gap-8 lg:grid-cols-[1.5fr_1fr]">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              {t(
                "landing.cta.title",
                "Start describing your facility's desired state today.",
              )}
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-white/75">
              {t(
                "landing.cta.body",
                "Open a free workspace, drop in your first building or pitch, and share the public link. You'll see your first report — and your first triage — within a day.",
              )}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/register"
                className="inline-flex h-11 items-center justify-center rounded-md bg-brand-500 px-5 text-sm font-semibold text-white shadow-brand transition hover:bg-brand-400"
              >
                {t("landing.cta.primary", "Create a workspace")}
              </Link>
              <Link
                to="/login"
                className="inline-flex h-11 items-center justify-center rounded-md bg-white/10 px-5 text-sm font-semibold text-white ring-1 ring-inset ring-white/25 transition hover:bg-white/15"
              >
                {t("landing.cta.secondary", "I already have one")}
              </Link>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <img
              src="/brand/logo-256.png"
              alt=""
              width={180}
              height={180}
              className="h-44 w-44 opacity-95 drop-shadow-[0_12px_30px_rgba(95,169,250,0.35)]"
              draggable={false}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- icons (inline so we don't add a dep) ---------- */

function BlueprintIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
    >
      <path d="M4 5h16v14H4z" />
      <path d="M4 9h6v6H4z" />
      <path d="M14 5v14" />
      <path d="M14 12h6" />
    </svg>
  );
}

function SignalIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
    >
      <circle cx="12" cy="12" r="2" />
      <path d="M8.5 8.5a5 5 0 0 0 0 7" />
      <path d="M15.5 15.5a5 5 0 0 0 0-7" />
      <path d="M5.5 5.5a9 9 0 0 0 0 13" />
      <path d="M18.5 18.5a9 9 0 0 0 0-13" />
    </svg>
  );
}

function AgentIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
    >
      <path d="M12 3v3" />
      <rect x="5" y="6" width="14" height="12" rx="3" />
      <circle cx="9.5" cy="12" r="1.2" />
      <circle cx="14.5" cy="12" r="1.2" />
      <path d="M9 16h6" />
      <path d="M3 12h2M19 12h2" />
    </svg>
  );
}
